import cx from 'classnames';

import Render from 'components/render';
import { TOTAL_EQUITY_DECIMAL, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { LOCALES } from 'constants/dateTime';
import { CURRENCY_TYPE } from 'constants/enums';
import { formatFiat } from 'helpers/currency';
import {
  addZeroesUntilCorrectPrecision,
  convertExponentialToDecimal,
  cropAfterDecimals,
  getBalanceBySymbol,
} from 'helpers/utils';
import i18n from 'i18n/config';
import { balanceBySymbolSelector, enableWalletsSelector } from 'selectors/walletSelector';
import { useAppSelector } from 'storeHooks';
import { AngleDown, AngleUp } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import CollapsibleCard from 'UIKit/CollapsibleCard';
import DeltaButton from 'UIKit/DeltaButton';

import { TotalEquity } from '../computeTotalBalance';
import BlockedMargin from './BlockedMargin';
import styles from './indianWallet.module.scss';
import TooltipWrapper from './TooltipWrapper';
import UnRealisedCashflow from './UnRealisedCashflow';

const headerAccountValue = i18n.t('headerFooter:header.wallet.tradingWallet');
const headerAccountBalance = i18n.t('headerFooter:header.wallet.walletBalance');
const accountBalancePopOverIndianExchange = i18n.t(
  'headerFooter:header.wallet.accountBalancePopOverIndianExchange'
);

const AccountValueCollapsableCard = ({
  showAmount = true,
  className = '',
}: {
  showAmount: boolean;
  className?: string;
}) => {
  const balances = useAppSelector(enableWalletsSelector);
  const allBalances = useAppSelector(balanceBySymbolSelector);

  const usdBalanceObject = balances?.USD ?? {};

  const usdAssetMinPrecision = usdBalanceObject?.asset?.minimum_precision ?? 2;

  const { balance, balance_inr: balanceInr } = getBalanceBySymbol(
    VANILLA_SETTLING_ASSET,
    allBalances
  ) ?? {
    balance: 0,
    balance_inr: 0,
  };

  const accountBalanceFormattedInUSD = formatFiat(
    CURRENCY_TYPE.USD,
    convertExponentialToDecimal(
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(Number(balance), usdAssetMinPrecision),
        usdAssetMinPrecision
      )
    ),
    LOCALES.GLOBAL,
    {
      maximumFractionDigits: TOTAL_EQUITY_DECIMAL,
    }
  );

  const accountBalanceFormattedInINR = formatFiat(
    CURRENCY_TYPE.INR,
    cropAfterDecimals(
      addZeroesUntilCorrectPrecision(Number(balanceInr), usdAssetMinPrecision),
      usdAssetMinPrecision
    ),
    LOCALES.INDIA,
    {
      maximumFractionDigits: TOTAL_EQUITY_DECIMAL,
    }
  );

  return (
    <CollapsibleCard
      expandedHeight="fit-content"
      className={cx(styles.collapsibleCardContainer, {
        [className]: className,
      })}
      expandedClassName={styles.expandedCardContainer}
    >
      {(toggleCollapsed, isCollapsed) => (
        <>
          <DeltaButton
            data-testid="account-balance-btn"
            fullWidth
            useBasicButton
            transparentBg
            onClick={toggleCollapsed}
            className={cx(
              'naked flex-set pointer',
              styles['label-value-container'],
              styles.accountValueCollapsableButton,
              { [styles.accountValueExpandedCard]: !isCollapsed }
            )}
            wrapperClassName="flex-set flex--content-center flex--align-items-start flex--content-between flex--full-width-item"
          >
            <Box className={styles.headerAccountValue}>
              {headerAccountValue}
              <span
                data-testid="collapse-contract-type-filter"
                className={cx(styles.collapseIcons, {
                  [styles.collapsedIcon]: isCollapsed,
                })}
              >
                {isCollapsed ? <AngleDown /> : <AngleUp />}
              </span>
            </Box>
            <Box className={styles.value}>
              <Box className={styles.inr}>
                <Render when={showAmount} else="******">
                  <TotalEquity isInr isForWalletIndianExchange />
                </Render>
              </Box>
              <Box className={styles.usd}>
                <Render when={showAmount} else="******">
                  <TotalEquity isForWalletIndianExchange />
                </Render>
              </Box>
            </Box>
          </DeltaButton>

          <Box
            renderWhen={!isCollapsed}
            className={`${styles.accountValueExpandedCardContainer}`}
            data-testid="account-value-expanded"
          >
            {/* Account Balance */}
            <Box
              renderWhen={!isCollapsed}
              className={styles['label-value-container']}
              data-testid="account-balance"
            >
              <Box className={styles.label}>
                <TooltipWrapper
                  title={headerAccountBalance}
                  body={accountBalancePopOverIndianExchange}
                />
              </Box>
              <Box className={styles.value}>
                <Box className={styles.inr}>
                  <Render when={showAmount} else="******">
                    {accountBalanceFormattedInINR}
                  </Render>
                </Box>
                <Box className={styles.usd}>
                  <Render when={showAmount} else="******">
                    {accountBalanceFormattedInUSD}
                  </Render>
                </Box>
              </Box>
            </Box>

            {/* UNREALIZED CASHFLOW */}
            <UnRealisedCashflow showAmount={showAmount} />

            {/* Blocked Margin */}
            <BlockedMargin showAmount={showAmount} />
          </Box>
        </>
      )}
    </CollapsibleCard>
  );
};

export default AccountValueCollapsableCard;
